import { EndOfTreatmentForm as EndOfTreatmentFormDto, LookupEotReasonType, LookupNumberDoseReductionsType, LookupYesNoUnknownType, Permission } from "@/api/dtos";
import { FormPatientDetails } from "@/components/common/PatientDetails";
import { crfReadOnly } from "@/utils/CrfFormUtils";
import { useTheme, useMediaQuery, Grid2, Tooltip, TooltipProps } from "@mui/material";
import { ConditionMode, useFieldState, ValidateOn } from '@ngt/forms-core';
import { Autocomplete, CollaboratingGroupContext, CrfCondition, CrfForm, DatePicker, FormBreadcrumbs, GroupedField, GroupedFields, ICrfConditionParameters, InstitutionContext, MasterGroupContext, Numeric, PageLayout, PatientContext, RouteLoading, Select, Text, ToggleButtonGroup } from '@ngt/opms';
import { usePermissionsByIds } from "@ngt/opms-bctapi";
import { FC, useCallback, useContext, useMemo } from "react";

interface IEndOfTreatmentFormProps {

}

const eotReasonsRequiringSpecification = [
    LookupEotReasonType.PatientDecision,
    LookupEotReasonType.InvestigatorDecision,
    LookupEotReasonType.Other
]

interface IFieldDependentTooltipProps extends Omit<TooltipProps, 'title'> {
    valueMapping: Record<any, string>
}

const FieldDependentToolTip: FC<IFieldDependentTooltipProps> = ({
    valueMapping,
    children,
    ...props
}) => {

    const {value} = useFieldState({value: true})
    const tooltipValue = useMemo(() => valueMapping[value], [value]);

    return (
        <>
        {
            tooltipValue ?
                <Tooltip
                    title={tooltipValue}
                    {...props}
                >
                    {children}
                </Tooltip> :
                <>{children}</>
        }
        </>
    )
}

const EndOfTreatmentForm: FC<IEndOfTreatmentFormProps> = () => {

    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: collaboratingGroup } = useContext(CollaboratingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: patient } = useContext(PatientContext);
    const theme = useTheme();

    const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

    const EotReasonInputComponent = useCallback((props: any) => {
        return (
            <FieldDependentToolTip 
                placement={isSmall ? "top" : "left"}
                valueMapping={{
                    [LookupEotReasonType.Toxicity]: 
                        "If reason for EOT is Toxicity, please enter the details of the " + 
                        "Toxicity event that led to treatment permanently ceasing in the " + 
                        "Adverse Event section of the EOT Visit Details form."
                }} 
                children={<Grid2 {...props} />}
            />
        )
    }, [isSmall]);
    // const eotReasonPatientDecision = useCallback((params: ICrfConditionParameters<EndOfTreatmentFormDto>) => 
    //     params.formState?.value?.eotReason === LookupEotReasonType.PatientDecision,
    // []);

    const eotReasonToxicity = useCallback((params: ICrfConditionParameters<EndOfTreatmentFormDto>) => 
        params.formState?.value?.eotReason === LookupEotReasonType.Toxicity,
    []);

    const eotReasonDeath = useCallback((params: ICrfConditionParameters<EndOfTreatmentFormDto>) => 
        params.formState?.value?.eotReason === LookupEotReasonType.Death,
    []);

    const eotReasonProgressiveDisease = useCallback((params: ICrfConditionParameters<EndOfTreatmentFormDto>) => 
        params.formState?.value?.eotReason === LookupEotReasonType.ProgressiveDisease,
    []);

    const eotReasonNeedsSpecifying = useCallback((params: ICrfConditionParameters<EndOfTreatmentFormDto>) => 
        !!params.formState?.value?.eotReason && eotReasonsRequiringSpecification.includes(params.formState?.value?.eotReason),
    []);

    const doseReduction = useCallback((params: ICrfConditionParameters<EndOfTreatmentFormDto>) => 
        params.formState?.value?.doseReduction === LookupYesNoUnknownType.Yes,
    []);

    const numberDoseReductionsOther = useCallback((params: ICrfConditionParameters<EndOfTreatmentFormDto>) => 
        params.formState?.value?.numberDoseReductions === LookupNumberDoseReductionsType.Other,
    []);

    const numberDoseReductionsAtLeastOne = useCallback((params: ICrfConditionParameters<EndOfTreatmentFormDto>) => 
        (params.formState?.value?.numberDoseReductions ?? 0) >= LookupNumberDoseReductionsType.One,
    []);

    const numberDoseReductionsAtLeastTwo = useCallback((params: ICrfConditionParameters<EndOfTreatmentFormDto>) => 
        (params.formState?.value?.numberDoseReductions ?? 0) >= LookupNumberDoseReductionsType.Two,
    []);

    const numberDoseReductionsAtLeastThree = useCallback((params: ICrfConditionParameters<EndOfTreatmentFormDto>) => 
        (params.formState?.value?.numberDoseReductions ?? 0) >= LookupNumberDoseReductionsType.Three,
    []);

    const {
        permissions: [
            canUpdateCrf
        ],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.OpmsCrfUpdate
        ], 
        masterGroup?.id, 
        collaboratingGroup?.id, 
        institution?.id, 
        patient?.id
    );

    if (permissionsLoading) {
        return (
            <RouteLoading />
        );
    }
    
    return (
        <PageLayout breadcrumbs={<FormBreadcrumbs />}>
            <CrfForm 
                validateOn={ValidateOn.onChange}
                readOnly={crfReadOnly(canUpdateCrf)}
                enableQueries
            >
                <FormPatientDetails />
                <GroupedField
                    propertyPath="lastCycleDate"
                    format={"dd-MMM-yyyy"}
                    component={DatePicker}
                    FieldGridProps={{
                        size: {
                            xl: 6,
                            lg: 6,
                            md: 6,
                            sm: 6,
                            xs: 12
                        }
                    }}
                />
                <GroupedField 
                    propertyPath="totalCyclesReceived"
                    component={Numeric}
                    FieldGridProps={{
                        size: {
                            xl: 6,
                            lg: 6,
                            md: 6,
                            sm: 6,
                            xs: 12
                        }
                    }}
                />
                <GroupedFields>
                    <GroupedField
                        propertyPath="eotReason"
                        component={Autocomplete}
                        FieldGridProps={{
                            component: (props) => <EotReasonInputComponent {...props} />,
                            size: {
                                xl: 6,
                                lg: 6,
                                md: 6,
                                sm: 6,
                                xs: 12
                            }
                        }}
                    />
                    <CrfCondition
                        condition={eotReasonNeedsSpecifying}
                        mode={ConditionMode.Show}
                    >
                        <GroupedField
                            propertyPath="eotOtherSpecify"
                            component={Text}
                            FieldGridProps={{
                                size: {
                                    xl: 6,
                                    lg: 6,
                                    md: 6,
                                    sm: 6,
                                    xs: 12
                                }
                            }}
                        />
                    </CrfCondition>
                </GroupedFields>
                <CrfCondition
                    condition={eotReasonToxicity}
                    mode={ConditionMode.Show}
                >
                    <GroupedField
                        propertyPath="doseReduction"
                        component={ToggleButtonGroup}
                        FieldGridProps={{
                            size: {
                                xl: 6,
                                lg: 6,
                                md: 6,
                                sm: 6,
                                xs: 12
                            }
                        }}
                    />
                </CrfCondition>
                <CrfCondition
                    condition={doseReduction}
                    mode={ConditionMode.Show}
                >
                    <GroupedField
                        propertyPath="numberDoseReductions"
                        component={Select}
                        FieldGridProps={{
                            size: {
                                xl: 6,
                                lg: 6,
                                md: 6,
                                sm: 6,
                                xs: 12
                            }
                        }}
                    />
                </CrfCondition>
                <CrfCondition
                    condition={numberDoseReductionsOther}
                    mode={ConditionMode.Show}
                >
                    <GroupedField
                        propertyPath="numberDoseReductionsOtherSpecify"
                        component={Text}
                        FieldGridProps={{
                            size: {
                                xl: 6,
                                lg: 6,
                                md: 6,
                                sm: 6,
                                xs: 12
                            }
                        }}
                    />
                </CrfCondition>
                <CrfCondition
                    condition={numberDoseReductionsAtLeastOne}
                    mode={ConditionMode.Show}
                >
                    <GroupedField
                        propertyPath="numCyclesBeforeFirstReduction"
                        component={Numeric}
                        FieldGridProps={{
                            size: {
                                xl: 6,
                                lg: 6,
                                md: 6,
                                sm: 6,
                                xs: 12
                            }
                        }}
                    />
                </CrfCondition>
                <CrfCondition
                    condition={numberDoseReductionsAtLeastTwo}
                    mode={ConditionMode.Show}
                >
                    <GroupedField
                        propertyPath="numCyclesBeforeSecondReduction"
                        component={Numeric}
                        FieldGridProps={{
                            size: {
                                xl: 6,
                                lg: 6,
                                md: 6,
                                sm: 6,
                                xs: 12
                            }
                        }}
                    />
                </CrfCondition>
                <CrfCondition
                    condition={numberDoseReductionsAtLeastThree}
                    mode={ConditionMode.Show}
                >
                    <GroupedField
                        propertyPath="numCyclesBeforeThirdReduction"
                        component={Numeric}
                        FieldGridProps={{
                            size: {
                                xl: 6,
                                lg: 6,
                                md: 6,
                                sm: 6,
                                xs: 12
                            }
                        }}
                    />
                </CrfCondition>
                <CrfCondition
                    condition={numberDoseReductionsOther}
                    mode={ConditionMode.Show}
                >
                    <GroupedField
                        propertyPath="doseReductionNumCyclesOther"
                        component={Text}
                        multiline
                        rows={4}
                        FieldGridProps={{
                            size: {
                                xl: 6,
                                lg: 6,
                                md: 6,
                                sm: 6,
                                xs: 12
                            }
                        }}
                    />
                </CrfCondition>
                <CrfCondition
                    condition={eotReasonDeath}
                    mode={ConditionMode.Show}
                >
                    <GroupedField
                        propertyPath="dateOfDeath"
                        component={DatePicker}
                        format={"dd-MMM-yyyy"}
                        FieldGridProps={{
                            size: {
                                xl: 6,
                                lg: 6,
                                md: 6,
                                sm: 6,
                                xs: 12
                            }
                        }}
                    />
                </CrfCondition>
                <CrfCondition
                    condition={eotReasonProgressiveDisease}
                    mode={ConditionMode.Show}
                >
                    <GroupedField
                        propertyPath="dateOfProgression"
                        component={DatePicker}
                        format={"dd-MMM-yyyy"}
                        FieldGridProps={{
                            size: {
                                xl: 6,
                                lg: 6,
                                md: 6,
                                sm: 6,
                                xs: 12
                            }
                        }}
                    />
                </CrfCondition>
                {/*<CrfCondition
                    condition={eotReasonPatientDecision}
                    mode={ConditionMode.Show}
                >
                    <GroupedField
                        propertyPath="dateOfConsentWithdrawal"
                        component={DatePicker}
                        FieldGridProps={{
                            size: {
                                xl: 6,
                                lg: 6,
                                md: 6,
                                sm: 6,
                                xs: 12
                            }
                        }}
                    />
                </CrfCondition>
                <CrfCondition
                    condition={eotReasonPatientDecision}
                    mode={ConditionMode.Show}
                >
                    <GroupedField
                        propertyPath="consentedToContinuedSampleUse"
                        component={RadioGroup}
                        FieldGridProps={{
                            size: {
                                xl: 6,
                                lg: 6,
                                md: 6,
                                sm: 6,
                                xs: 12
                            }
                        }}
                    />
                </CrfCondition>
                <CrfCondition
                    condition={eotReasonPatientDecision}
                    mode={ConditionMode.Show}
                >
                    <GroupedField
                        propertyPath="consentedToFutureUnspecifiedSampleUse"
                        component={RadioGroup}
                        FieldGridProps={{
                            size: {
                                xl: 6,
                                lg: 6,
                                md: 6,
                                sm: 6,
                                xs: 12
                            }
                        }}
                    />
                </CrfCondition> */}
                <GroupedField
                    propertyPath="response"
                    component={Autocomplete}
                    FieldGridProps={{
                        size: {
                            xl: 6,
                            lg: 6,
                            md: 6,
                            sm: 6,
                            xs: 12
                        }
                    }}
                />
                <GroupedField
                    propertyPath="lastDateOfDiseaseAssessments"
                    component={DatePicker}
                    format="dd-MMM-yyyy"
                    FieldGridProps={{
                        size: {
                            xl: 6,
                            lg: 6,
                            md: 6,
                            sm: 6,
                            xs: 12
                        }
                    }}
                />
                <GroupedField
                    propertyPath="additionalInformation"
                    component={Text}
                    multiline
                    rows={4}
                    FieldGridProps={{
                        size: {
                            xl: 6,
                            lg: 6,
                            md: 6,
                            sm: 6,
                            xs: 12
                        }
                    }}
                />
            </CrfForm>
        </PageLayout>
    );
}

export default EndOfTreatmentForm;